/**
 * Fishing View
 * Desktop (md+): Three-column layout — Bait & Stats & Trap (left) | Fishing (center) | Discovery (right)
 * Mobile (<md): Vertical stack — Fishing, Bait, Trap, Stats always visible; Discovery collapsible.
 */

import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '@/state';
import { CompactDiscoveryPanel } from './CompactDiscoveryPanel';
import { FishingCenterPanel } from './FishingCenterPanel';
import { FishingStatsPanel } from './FishingStatsPanel';
import { BaitPanel } from './BaitPanel';
import { TrapPanel } from './TrapPanel';
import { CollapsibleSection } from '@/components/common/CollapsibleSection';
import { CollectOverlay } from '@/components/collect';
import type { CollectScenarioConfig } from '@/components/collect';
import {
  GameEvent,
  TrapPullResult,
  QualityColors,
  QualityNames,
  QualityMultipliers,
} from '@/types';
import { eventBus } from '@/services/EventBus';
import type { CollectItemData } from '@/components/collect';

/**
 * Build a CollectScenarioConfig from a TrapPullResult.
 * Maps trap creatures → collectible items with quality-colored borders + glow.
 */
function buildTrapCollectScenario(
  result: TrapPullResult,
  onComplete: () => void,
): CollectScenarioConfig {
  const items: CollectItemData[] = result.creatures.map((creature, index) => {
    const { fish, definition } = creature;
    const qualityMult = QualityMultipliers[fish.quality] ?? 1.0;
    const value = Math.round(definition.baseValue * qualityMult);

    return {
      id: `trap-creature-${index}`,
      emoji: definition.emoji ?? '🐟',
      label: definition.name,
      sublabel: QualityNames[fish.quality],
      sublabelColor: QualityColors[fish.quality],
      borderColor: QualityColors[fish.quality],
      glowColor: QualityColors[fish.quality],
      value,
      size: 64,
      restitution: 0.35,
    };
  });

  return {
    items,
    title: 'Trap Haul!',
    spawnOrigin: { x: 0.5, y: -0.05 },
    throwConfig: {
      minVX: -280,
      maxVX: 280,
      minVY: 100,
      maxVY: 450,
      minAngular: -200,
      maxAngular: 200,
      staggerDelayMs: 80,
    },
    autoCollectMs: 8000,
    onComplete,
  };
}

export function FishingView() {
  const [collectScenario, setCollectScenario] = useState<CollectScenarioConfig | null>(null);

  const handleNavigateToMarket = (marketId: string) => {
    useGameStore.setState((draft) => {
      draft.currentTab = 'markets';
      draft.targetMarketId = marketId;
    });
  };

  const handleDismissCollect = useCallback(() => {
    setCollectScenario(null);
  }, []);

  // Listen for TRAP_PULLED event to show collect overlay
  useEffect(() => {
    const unsub = eventBus.on(GameEvent.TRAP_PULLED, (result: TrapPullResult) => {
      setCollectScenario(buildTrapCollectScenario(result, handleDismissCollect));
    });
    return unsub;
  }, [handleDismissCollect]);

  return (
    <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
      {/* Left sidebar — Bait, Stats, Trap (desktop) */}
      <aside className="hidden md:flex md:flex-col md:w-56 lg:w-72 xl:w-80 border-r border-gray-700 overflow-y-auto shrink-0">
        <BaitPanel />
        <div className="border-t border-gray-700">
          <FishingStatsPanel />
        </div>
        <div className="border-t border-gray-700">
          <TrapPanel />
        </div>
      </aside>

      {/* Center — Fishing action area (always visible, scrollable) */}
      <div className="flex-1 overflow-y-auto min-w-0">
        <FishingCenterPanel onNavigateToMarket={handleNavigateToMarket} />

        {/* Mobile: bait + trap + stats always visible, discovery collapsible */}
        <div className="md:hidden px-6 pb-6 space-y-3">
          <BaitPanel />
          <TrapPanel />
          <FishingStatsPanel />
          <CollapsibleSection title="Discovered Fish" icon="📖">
            <CompactDiscoveryPanel />
          </CollapsibleSection>
        </div>
      </div>

      {/* Right sidebar — Discovery (desktop) */}
      <aside className="hidden md:flex md:flex-col md:w-56 lg:w-72 xl:w-80 border-l border-gray-700 overflow-y-auto shrink-0">
        <CompactDiscoveryPanel />
      </aside>

      {/* Collect overlay (physics mini-game) */}
      <CollectOverlay scenario={collectScenario} />
    </div>
  );
}
